'use client';
import { Button } from '@/components/ui/button';
import type React from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import MultipleSelector, { type Option } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAccountSettings } from '@/hooks/useAccountSettings';
import { useSetupProfile } from '@/hooks/useSetupProfile';
import { errorHandler } from '@/lib/errorHandler';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store';
import type { ApiErrorResponse } from '@/types/api/common';
import { zodResolver } from '@hookform/resolvers/zod';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Cropper from 'react-easy-crop';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const optionSchema = z.object({
  label: z.string(),
  value: z.string(),
  disable: z.boolean().optional(),
});

const formSchema = z.object({
  name: z.string().trim().min(2, {
    message: 'Name must be at least 2 characters long',
  }),
  email: z.optional(z.string()), // It is optional because the user can't change their email
  userName: z.optional(z.string()), // It is optional because the user can't change their username
  occupation: z.optional(z.string()),
  industry: z.optional(z.string()),
  areaOfInterest: z.array(optionSchema).min(2, {
    message: 'Please select at least two area of interest',
  }),
  avatarUrl: z.optional(z.string()),
});

// Helper function to create a cropped image
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = document.createElement('img');
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error: ErrorEvent) => reject(error));
    image.crossOrigin = 'anonymous';
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number }
): Promise<Blob> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // Set canvas size to the cropped size
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Draw the cropped image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // Convert canvas to blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) throw new Error('Canvas is empty');
      resolve(blob);
    }, 'image/jpeg');
  });
};

export const ProfileAccountSettingsForm = () => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      userName: '',
      email: '',
      occupation: '',
      industry: '',
      areaOfInterest: [],
      avatarUrl: '',
    },
  });

  const setUser = useAuthStore((state) => state.setUser);
  const user = useAuthStore((state) => state.user);

  // Image cropping state
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);

  const {
    getAccountSettingsApi,
    updateAccountSettingsApi,
    getAvatarUploadUrlApi,
    uploadAvatarApi,
  } = useAccountSettings({
    loadGetAccountSettings: true,
  });

  const { data: accountSettings } = getAccountSettingsApi;

  const { getOccupationApi, getIndustryApi, getCategoriesApi } =
    useSetupProfile({
      loadOccupation: true,
      loadIndustry: true,
      loadCategories: true,
    });

  // Grouped occupation options: flatten subOccupations and add group label
  const occupationOptions: Option[] = useMemo(() => {
    if (!Array.isArray(getOccupationApi.data?.data)) return [];
    // If grouped, flatten; else fallback to flat
    return getOccupationApi.data.data.flatMap((parent: any) => {
      if (Array.isArray(parent.subOccupations)) {
        return parent.subOccupations.map((item: any) => ({
          label: item.name,
          value: item.id,
          group: parent.name,
        }));
      } else {
        // Fallback for flat data
        return [{ label: parent.name, value: parent.id }];
      }
    });
  }, [getOccupationApi.data]);

  // Grouped industry options: flatten subIndustries and add group label
  const industryOptions: Option[] = useMemo(() => {
    if (!Array.isArray(getIndustryApi.data?.data)) return [];
    return getIndustryApi.data.data.flatMap((parent: any) => {
      if (Array.isArray(parent.subIndustries)) {
        return parent.subIndustries.map((item: any) => ({
          label: item.name,
          value: item.id,
          group: parent.name,
        }));
      } else {
        // Fallback for flat data
        return [{ label: parent.name, value: parent.id }];
      }
    });
  }, [getIndustryApi.data]);

  const areaOfInterestOptions: Option[] = useMemo(() => {
    const grouped = Object.entries(getCategoriesApi.data?.data || {}).flatMap(
      ([key, value]) =>
        value.map((item) => ({ value: item.id, label: item.name, group: key }))
    );

    return grouped ?? [];
  }, [getCategoriesApi.data]);

  useEffect(() => {
    if (accountSettings?.data) {
      const selectedAreaOfInterest = areaOfInterestOptions.filter((option) =>
        accountSettings.data.areasOfInterest?.some(
          (item) => item.id === option.value
        )
      );

      const selectedIndustry = industryOptions.find(
        (item) => item.value === accountSettings.data.industry?.id
      );

      const selectedOccupation = occupationOptions.find(
        (item) => item.value === accountSettings.data.occupation?.id
      );

      // Get current form values to preserve unchanged fields
      const currentValues = form.getValues();

      form.reset({
        name: accountSettings.data.name,
        email: accountSettings.data.email,
        userName: accountSettings.data.username,
        occupation: selectedOccupation?.value || currentValues.occupation,
        industry: selectedIndustry?.value || currentValues.industry,
        areaOfInterest: selectedAreaOfInterest,
        avatarUrl: accountSettings.data.profilePicture || '',
      });
    }
  }, [
    accountSettings,
    areaOfInterestOptions,
    occupationOptions,
    industryOptions,
    form,
  ]);

  const isUploading =
    getAvatarUploadUrlApi.isPending || uploadAvatarApi.isPending;

  const onCropComplete = useCallback(
    (croppedArea: any, croppedAreaPixels: any) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Create a URL for the image
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setIsCropperOpen(true);
    };
    reader.readAsDataURL(file);

    // Reset the input value so the same file can be selected again
    e.target.value = '';
  };

  const handleCropCancel = () => {
    setIsCropperOpen(false);
    setImageSrc(null);
  };

  const handleCropConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    try {
      // Get the cropped image as a blob
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);

      // Close the cropper
      setIsCropperOpen(false);
      setImageSrc(null);

      // Upload the cropped image
      await uploadImage(croppedImage);
    } catch (error) {
      toast.error('Error cropping image');
      console.error(error);
    }
  };

  const uploadImage = async (file: Blob) => {
    try {
      // 1. Get the signed URL from your API
      const response = await getAvatarUploadUrlApi.mutateAsync(file.type);

      if (!response) {
        toast.error('Failed to get upload URL');
        return;
      }

      const { url, s3Url } = response.data;

      // Convert Blob to File
      const fileToUpload = new File([file], 'profile-image.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      // 2. Upload the file to S3
      await uploadAvatarApi.mutateAsync({ url, file: fileToUpload });

      // 3. Update the form with the new avatar URL
      form.setValue('avatarUrl', s3Url);
    } catch (error) {
      toast.error(errorHandler(error as ApiErrorResponse));
    }
  };

  function onSubmit(values: z.infer<typeof formSchema>) {
    // Get current form values to ensure we have the latest state
    const currentValues = form.getValues();

    const formData = {
      name: values.name,
      occupation: values.occupation,
      industry: currentValues.industry, // Use current form value for industry
      areaOfInterest: values.areaOfInterest.map((item) => item.value),
      avatarUrl: values.avatarUrl || '',
    };

    updateAccountSettingsApi.mutate(formData, {
      onSuccess: () => {
        toast.success('Profile updated successfully');
        // Preserve the form values after successful update
        form.reset({
          ...currentValues,
          name: values.name,
          occupation: values.occupation,
          areaOfInterest: values.areaOfInterest,
          avatarUrl: values.avatarUrl,
        });
      },
      onError: (error) => {
        toast.error(errorHandler(error));
      },
    });
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <h2 className="font-bold text-lg">Personal info</h2>
        <p className="text-sm">Update your personal info</p>
      </div>

      <div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label
                htmlFor="avatar-upload"
                className="cursor-pointer block relative"
              >
                <Image
                  src={form.watch('avatarUrl') || '/img-upload.svg'}
                  alt="profile-image-upload"
                  width={72}
                  height={72}
                  className={cn(
                    'rounded-full object-cover',
                    isUploading && 'blur-xs'
                  )}
                />
                {isUploading && (
                  <div className="absolute h-[72px] w-[72px] inset-0 flex items-center justify-center bg-black/50 bg-opacity-50 rounded-full">
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                  </div>
                )}
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileSelect}
                  disabled={isUploading}
                />
              </label>
            </div>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      readOnly
                      disabled
                      placeholder="Enter your email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="userName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input
                      readOnly
                      disabled
                      placeholder="Enter your username"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="industry"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Industry (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full truncate">
                        <SelectValue placeholder="Choose item" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {/* Grouped options by parent industry */}
                      {(() => {
                        const groups: Record<string, Option[]> = {};
                        industryOptions.forEach((opt) => {
                          if (
                            typeof opt.group === 'string' &&
                            opt.group.length > 0
                          ) {
                            if (!groups[opt.group]) groups[opt.group] = [];
                            groups[opt.group]!.push(opt);
                          } else {
                            if (!groups['Other']) groups['Other'] = [];
                            groups['Other']!.push(opt);
                          }
                        });
                        return Object.entries(groups).map(
                          ([groupName, opts]) => (
                            <div key={groupName}>
                              <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50">
                                {groupName}
                              </div>
                              {opts.map((item) => (
                                <SelectItem key={item.value} value={item.value}>
                                  {item.label}
                                </SelectItem>
                              ))}
                            </div>
                          )
                        );
                      })()}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="occupation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Job Role (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full truncate">
                        <SelectValue placeholder="Choose item" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {/* Grouped options by parent occupation */}
                      {(() => {
                        const groups: Record<string, Option[]> = {};
                        occupationOptions.forEach((opt) => {
                          if (
                            typeof opt.group === 'string' &&
                            opt.group.length > 0
                          ) {
                            if (!groups[opt.group]) groups[opt.group] = [];
                            groups[opt.group]!.push(opt);
                          } else {
                            if (!groups['Other']) groups['Other'] = [];
                            groups['Other']!.push(opt);
                          }
                        });
                        return Object.entries(groups).map(
                          ([groupName, opts]) => (
                            <div key={groupName}>
                              <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50">
                                {groupName}
                              </div>
                              {opts.map((item) => (
                                <SelectItem key={item.value} value={item.value}>
                                  {item.label}
                                </SelectItem>
                              ))}
                            </div>
                          )
                        );
                      })()}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="areaOfInterest"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex flex-wrap items-center gap-x-2">
                    Area of interest
                  </FormLabel>
                  <FormControl>
                    <MultipleSelector
                      {...field}
                      defaultOptions={areaOfInterestOptions}
                      hidePlaceholderWhenSelected
                      options={areaOfInterestOptions}
                      placeholder="Select Area of Interest"
                      groupBy="group"
                      onChange={(options) => {
                        field.onChange(options);
                      }}
                      emptyIndicator={
                        <p className="text-center text-lg leading-10 text-gray-600 dark:text-gray-400">
                          {form.watch('areaOfInterest').length > 0
                            ? 'All options selected'
                            : 'No options available'}
                        </p>
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              isLoading={updateAccountSettingsApi.isPending}
              className="font-bold uppercase"
              type="submit"
            >
              SAVE
            </Button>
          </form>
        </Form>
      </div>

      {/* Image Cropper Dialog */}
      <Dialog open={isCropperOpen} onOpenChange={setIsCropperOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Crop Profile Image</DialogTitle>
          </DialogHeader>
          <div className="relative h-64 w-full">
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                cropShape="round"
              />
            )}
          </div>
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="flex-1">
              <label htmlFor="zoom" className="text-sm">
                Zoom: {zoom.toFixed(1)}x
              </label>
              <input
                id="zoom"
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              className="font-bold uppercase"
              variant="outline"
              onClick={handleCropCancel}
            >
              Cancel
            </Button>
            <Button className="font-bold uppercase" onClick={handleCropConfirm}>
              Apply
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
